import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import { policies, type PolicySettings } from '@/shared/api/resources';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { SkeletonText } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';

/**
 * "Configuracao da apolice" da empresa (P-APO-1).
 *
 * Quem gera o numero da apolice e quem gera o boleto sao decisoes do dono ainda
 * abertas (ADR 0039). Os dois comportamentos ficam disponiveis: numero informado na
 * emissao (o padrao) ou gerado pelo SeguraCore; boleto desligado (o padrao, sem
 * integracao bancaria) ou anexado a parcela. A troca vale dali em diante.
 */
export function PolicySettingsDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const atual = useQuery({
    queryKey: ['policy-settings'],
    queryFn: ({ signal }) => policies.settings(signal),
    enabled: open,
  });

  const [rascunho, setRascunho] = useState<PolicySettings | null>(null);
  const valor = rascunho ?? atual.data ?? null;

  const gravar = useMutation({
    mutationFn: (input: PolicySettings) => policies.updateSettings(input),
    onSuccess: () => {
      toast.success('Configuracao da apolice gravada.');
      setRascunho(null);
      void queryClient.invalidateQueries({ queryKey: ['policy-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
      onClose();
    },
  });

  const erro = gravar.error instanceof ApiError ? gravar.error : null;

  function campo<K extends keyof PolicySettings>(chave: K, novo: PolicySettings[K]) {
    if (!valor) return;
    setRascunho({ ...valor, [chave]: novo });
  }

  function fechar() {
    setRascunho(null);
    gravar.reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={fechar}
      title="Configuracao da apolice"
      description="Numeracao, boleto e lista de apolices da empresa."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={fechar}>
            Cancelar
          </Button>
          <Button
            disabled={!valor}
            loading={gravar.isPending}
            onClick={() => {
              if (valor) {
                gravar.mutate({
                  number_mode: valor.number_mode,
                  number_prefix:
                    valor.number_mode === 'automatico' ? valor.number_prefix : '',
                  billet_mode: valor.billet_mode,
                  show_pending_issuance: valor.show_pending_issuance,
                });
              }
            }}
          >
            Gravar
          </Button>
        </>
      }
    >
      {atual.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a configuracao">
          {atual.error.message}
        </Alert>
      ) : !valor ? (
        <SkeletonText lines={3} />
      ) : (
        <div className="flex flex-col gap-4">
          {gravar.isError && !fieldErrorOf(erro, 'number_prefix') ? (
            <Alert tone="danger" title="Nao foi possivel gravar">
              {gravar.error.message}
            </Alert>
          ) : null}
          <Select
            label="Numero da apolice gerado por"
            value={valor.number_mode}
            options={[
              {
                value: 'informado',
                label: 'Seguradora ou banco (informado na emissao)',
              },
              { value: 'automatico', label: 'SeguraCore (12 digitos na emissao)' },
            ]}
            onChange={(event) =>
              campo('number_mode', event.target.value as PolicySettings['number_mode'])
            }
          />
          {valor.number_mode === 'automatico' ? (
            <Field
              label="Prefixo do numero"
              inputMode="numeric"
              maxLength={6}
              value={valor.number_prefix}
              error={fieldErrorOf(erro, 'number_prefix')}
              onChange={(event) => campo('number_prefix', event.target.value)}
            />
          ) : null}
          <Select
            label="Boleto"
            value={valor.billet_mode}
            options={[
              { value: 'desligado', label: 'Desligado (sem integracao bancaria)' },
              { value: 'anexo', label: 'Anexado a parcela por quem tem a alcada' },
            ]}
            onChange={(event) =>
              campo('billet_mode', event.target.value as PolicySettings['billet_mode'])
            }
          />
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={valor.show_pending_issuance}
              onChange={(event) => campo('show_pending_issuance', event.target.checked)}
              className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
            />
            Mostrar as operacoes em emissao como Aguardando Emissao
          </label>
          {valor.updated_label ? (
            <p className="text-xs text-ink-500">Alterada por {valor.updated_label}.</p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
